import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SwitchRow } from '@/components/ui/switch';
import {
  SegmentRefreshMode,
  apiError,
  type CreateSegmentInput,
  type Segment,
  type SegmentDefinition,
  type SegmentPreviewResult,
  type SegmentRule,
  type SegmentVocabulary,
} from '../lib';
import SegmentRuleEditor from './SegmentRuleEditor';
import { ruleIsValid } from './segmentEditorLib';

export default function SegmentFormDialog({
  vocabulary,
  segment,
  onClose,
  onSaved,
}: {
  vocabulary: SegmentVocabulary;
  /** `null` creates; a row edits. */
  segment: Segment | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState(segment?.name ?? '');
  const [description, setDescription] = useState(segment?.description ?? '');
  const [match, setMatch] = useState<string>(segment?.definition.match ?? vocabulary.match[0]);
  const [rules, setRules] = useState<SegmentRule[]>(segment?.definition.rules ?? []);
  const [refreshMode, setRefreshMode] = useState<SegmentRefreshMode>(
    segment?.refreshMode ?? SegmentRefreshMode.SCHEDULED,
  );
  const [isActive, setIsActive] = useState(segment?.isActive ?? true);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const specByField = useMemo(
    () => new Map(vocabulary.fields.map((f) => [f.field, f])),
    [vocabulary.fields],
  );
  const allRulesValid = rules.every((r) => {
    const spec = specByField.get(r.field);
    return spec ? ruleIsValid(r, spec) : false;
  });
  const nameError = submitted && !name.trim() ? t('crm.segments.nameRequired') : undefined;
  const rulesError =
    submitted && rules.length === 0
      ? t('crm.segments.rulesRequired')
      : submitted && !allRulesValid
        ? t('crm.segments.rulesIncomplete')
        : undefined;

  // Debounced live preview, capped and interactive — the one on-demand path
  // ADR-0010 §3.2 describes (`POST /segments/preview`); everything saved is
  // materialised on a schedule instead.
  const [preview, setPreview] = useState<SegmentPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const definition: SegmentDefinition | null =
    rules.length > 0 && allRulesValid ? { match: match as SegmentDefinition['match'], rules } : null;

  useEffect(() => {
    if (!definition) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      api
        .post<SegmentPreviewResult>('/segments/preview', { definition })
        .then(({ data }) => {
          if (!cancelled) {
            setPreview(data);
            setPreviewError(null);
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setPreview(null);
            setPreviewError(apiError(e, t('crm.segments.previewFailed')));
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(definition)]);

  const save = async () => {
    setSubmitted(true);
    if (!name.trim() || !definition) return;
    setSaving(true);
    try {
      const body: CreateSegmentInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        definition,
        refreshMode,
        isActive,
      };
      if (segment) {
        await api.put(`/segments/${segment.id}`, body);
        toast.success(t('crm.segments.updated'));
      } else {
        await api.post('/segments', body);
        toast.success(t('crm.segments.created'));
      }
      onSaved();
    } catch (e) {
      toast.error(apiError(e, t('crm.segments.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(segment ? 'crm.segments.editSegment' : 'crm.segments.newSegment')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('crm.segments.name')} error={nameError} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('crm.segments.description')}>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div className="rounded-lg border p-3">
          <SegmentRuleEditor
            vocabulary={vocabulary}
            match={match}
            onMatchChange={setMatch}
            rules={rules}
            onRulesChange={setRules}
          />
          {rulesError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{rulesError}</p>}
        </div>

        <PreviewPanel loading={previewLoading} error={previewError} result={preview} hasRules={rules.length > 0} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('crm.segments.refreshMode')} hint={t('crm.segments.refreshModeHint')}>
            <Select
              value={refreshMode}
              onChange={(e) => setRefreshMode(e.target.value as SegmentRefreshMode)}
            >
              {Object.values(SegmentRefreshMode).map((mode) => (
                <option key={mode} value={mode}>
                  {t(`crm.segments.refreshModeValue.${mode}`)}
                </option>
              ))}
            </Select>
          </Field>
          <SwitchRow
            label={t('crm.segments.isActive')}
            hint={t('crm.segments.isActiveHint')}
            checked={isActive}
            onChange={setIsActive}
          />
        </div>

        {segment && (
          <p className="text-xs text-ink-muted">{t('crm.segments.editClearsMembership')}</p>
        )}
      </div>
    </Dialog>
  );
}

function PreviewPanel({
  loading,
  error,
  result,
  hasRules,
}: {
  loading: boolean;
  error: string | null;
  result: SegmentPreviewResult | null;
  hasRules: boolean;
}) {
  const { t } = useTranslation();
  if (!hasRules) return null;
  return (
    <div className="rounded-lg bg-subtle p-3 text-sm">
      {loading ? (
        <p className="text-ink-muted">{t('crm.segments.previewLoading')}</p>
      ) : error ? (
        <p className="text-red-600 dark:text-red-400">{error}</p>
      ) : result ? (
        <p>
          {t('crm.segments.previewCount', { count: result.memberCount })}
          {result.candidates > result.memberCount && (
            <span className="text-ink-muted">
              {' '}
              {t('crm.segments.previewCandidates', { count: result.candidates })}
            </span>
          )}
        </p>
      ) : (
        <p className="text-ink-muted">{t('crm.segments.previewHint')}</p>
      )}
    </div>
  );
}
